import React, { useState } from 'react';
import PropTypes from 'prop-types';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import ListSubheader from '@material-ui/core/ListSubheader';
import { useTheme, makeStyles } from '@material-ui/core/styles';
import { VariableSizeList } from 'react-window';
import { Typography } from '@material-ui/core';
import { Controller } from 'react-hook-form';

const LISTBOX_PADDING = 8; // px

function renderRow(props) {
	const { data, index, style } = props;
	return React.cloneElement(data[index], {
		style: {
			...style,
			top: style.top + LISTBOX_PADDING,
		},
	});
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef((props, ref) => {
	const outerProps = React.useContext(OuterElementContext);
	return <div ref={ref} {...props} {...outerProps} />;
});

function useResetCache(data) {
	const ref = React.useRef(null);
	React.useEffect(() => {
		if (ref.current != null) {
			ref.current.resetAfterIndex(0, true);
		}
	}, [data]);
	return ref;
}

// Adapter for react-window
const ListboxComponent = React.forwardRef(function ListboxComponent(
	props,
	ref
) {
	const { children, ...other } = props;
	const itemData = React.Children.toArray(children);
	const theme = useTheme();
	const smUp = useMediaQuery(theme.breakpoints.up('sm'), { noSsr: true });
	const itemCount = itemData.length;
	const itemSize = smUp ? 36 : 48;

	const getChildSize = (child) => {
		if (React.isValidElement(child) && child.type === ListSubheader) {
			return 48;
		}

		return itemSize;
	};

	const getHeight = () => {
		if (itemCount > 8) {
			return 8 * itemSize;
		}
		return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
	};

	const gridRef = useResetCache(itemCount);

	return (
		<div ref={ref}>
			<OuterElementContext.Provider value={other}>
				<VariableSizeList
					itemData={itemData}
					height={getHeight() + 2 * LISTBOX_PADDING}
					width="100%"
					ref={gridRef}
					outerElementType={OuterElementType}
					innerElementType="ul"
					itemSize={(index) => getChildSize(itemData[index])}
					overscanCount={5}
					itemCount={itemCount}
				>
					{renderRow}
				</VariableSizeList>
			</OuterElementContext.Provider>
		</div>
	);
});

ListboxComponent.propTypes = {
	children: PropTypes.node,
};

const useStyles = makeStyles({
	listbox: {
		boxSizing: 'border-box',
		'& ul': {
			padding: 0,
			margin: 0,
		},
	},
});

const renderGroup = (params) => [
	<ListSubheader key={params.key} component="div">
		{params.group}
	</ListSubheader>,
	params.children,
];

export default function Virtualize({defaultValue, options, label, id, handleChange }) { //carefull with name
	const classes = useStyles();

	return (
		<Autocomplete
			id={id}
			aria-label={navigator.userAgent.includes('Firefox') ? '' : label}
			disableListWrap
			classes={classes}
			ListboxComponent={ListboxComponent}
			getOptionLabel={(value) => value.name}
			renderGroup={renderGroup}
			options={options}
			groupBy={(option) => option.name[0].toUpperCase()}
			value={defaultValue ?? null}
			renderInput={(params) => <TextField {...params} label={label} />}
			onChange={handleChange}
			renderOption={(option) => {
				return <Typography noWrap>{option.name}</Typography>;
			}}
		/>
	);
}

export function VirtualizeControlled({
	options,
	label,
	id,
	handleChange,
	error,
	helperText,
	name,
	control,
	rules,
	defaultValue,
}) {
	const classes = useStyles();

	return (
		<Autocomplete
			id={id}
			aria-label={navigator.userAgent.includes('Firefox') ? '' : label}
			disableListWrap
			classes={classes}
			ListboxComponent={ListboxComponent}
			getOptionLabel={(value) => value.name || ''}
			renderGroup={renderGroup}
			options={options}
			groupBy={(option) => option.name[0].toUpperCase()}
			getOptionSelected={(option, value) => {
				return option.id === value.id;
			}}
			renderInput={(params) => (
				<Controller
					control={control}
					name={name}
					rules={rules}
					render={({ field }) => (
						<TextField
							{...params}
							label={label}
							fullWidth={true}
							error={error}
							helperText={helperText}
							id={name}
							{...field}
							placeholder={defaultValue?.name || ''}
						/>
					)}
				></Controller>
			)}
			onChange={handleChange}
			renderOption={(option) => {
				return <Typography noWrap>{option.name}</Typography>;
			}}
		/>
	);
}
